// runtime/api-3d/scene.js
// clearScene, setupScene, raycasting, stats, N64 effects

import * as THREE from 'three';
import { logger } from '../../logger.js';

export function sceneModule(ctx) {
  const {
    scene,
    camera,
    renderer,
    gpu,
    meshes,
    cartLights,
    materialCache,
    instancedMeshes,
    lodObjects,
  } = ctx;

  // These come from other modules — resolved at call-time via ctx
  function getDisposeMaterial() {
    return ctx.disposeMaterial;
  }
  function getRemoveLight() {
    return ctx.removeLight;
  }

  function clearScene() {
    const disposeMaterial = getDisposeMaterial();
    const removeLight = getRemoveLight();

    // Reset post-processing so the previous cart's bloom/FXAA/vignette don't leak.
    globalThis.nova64?.fx?.disableRetroEffects?.();

    logger.info(
      '🧹 Clearing 3D scene... meshes:',
      meshes.size,
      'lights:',
      cartLights.size,
      'materials:',
      materialCache.size
    );

    // Dispose all cart meshes
    for (const [, mesh] of meshes) {
      const object = mesh.__threeObject ?? mesh;
      scene.remove(object);
      if (object.geometry) object.geometry.dispose();
      if (object.material) {
        if (Array.isArray(object.material)) object.material.forEach(disposeMaterial);
        else disposeMaterial(object.material);
      }
    }
    meshes.clear();

    // Remove dynamic lights
    cartLights.forEach((_, id) => removeLight(id));
    cartLights.clear();

    // Flush material cache
    materialCache.forEach(disposeMaterial);
    materialCache.clear();

    // Remove any remaining scene children (e.g. from loadModel / direct additions).
    // Lights are intentionally skipped — the backend's default lights (gpu.lights.*)
    // stay in the scene so setAmbientLight / setLightDirection keep working after clear.
    for (let i = scene.children.length - 1; i >= 0; i--) {
      const child = scene.children[i];
      if (child.isLight) continue;
      scene.remove(child);
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) child.material.forEach(disposeMaterial);
        else disposeMaterial(child.material);
      }
    }

    // Clear fog so the previous scene's fog doesn't bleed into the next one.
    scene.fog = null;

    // Reset animated mesh registry in GPU backend
    if (gpu.clearAnimatedMeshes) gpu.clearAnimatedMeshes();

    // Dispose instanced meshes
    for (const { mesh } of instancedMeshes.values()) {
      scene.remove(mesh);
      mesh.geometry?.dispose();
      mesh.material?.dispose();
    }
    instancedMeshes.clear();

    // Dispose LOD objects
    for (const lod of lodObjects.values()) {
      scene.remove(lod);
      for (const { object } of lod.levels) {
        object.geometry?.dispose();
        object.material?.dispose();
      }
    }
    lodObjects.clear();

    logger.info('✅ Scene cleared completely');
  }

  /**
   * setupScene(opts) — One-call scene configuration.
   * opts: { camera, light, fog, skybox, effects }
   */
  function setupScene(opts = {}) {
    const cam = opts.camera ?? {};
    ctx.setCameraPosition(cam.x ?? 0, cam.y ?? 5, cam.z ?? 10);
    ctx.setCameraTarget(cam.targetX ?? 0, cam.targetY ?? 0, cam.targetZ ?? 0);
    if (cam.fov) ctx.setCameraFOV(cam.fov);

    const light = opts.light ?? {};
    if (light.direction) {
      const d = light.direction;
      ctx.setLightDirection(d[0] ?? -0.3, d[1] ?? -1, d[2] ?? -0.5);
    }
    if (light.color !== undefined) ctx.setLightColor(light.color);
    if (light.ambient !== undefined) ctx.setAmbientLight(light.ambient);

    if (opts.fog && opts.fog !== false) {
      ctx.setFog(opts.fog.color ?? 0x000000, opts.fog.near ?? 10, opts.fog.far ?? 50);
    }

    if (opts.skybox) {
      globalThis.nova64?.light?.createSpaceSkybox?.(opts.skybox);
    }

    if (opts.effects) {
      globalThis.nova64?.fx?.enableRetroEffects?.(
        typeof opts.effects === 'object' ? opts.effects : {}
      );
    }
  }

  function raycastFromCamera(x, y) {
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2(
      (x / renderer.domElement.clientWidth) * 2 - 1,
      -(y / renderer.domElement.clientHeight) * 2 + 1
    );
    raycaster.setFromCamera(mouse, camera);
    const objects = Array.from(meshes.values()).filter(m => m.type === 'Mesh');
    const hits = raycaster.intersectObjects(objects);
    if (hits.length > 0) {
      for (const [id, mesh] of meshes) {
        if (mesh === hits[0].object) {
          return { meshId: id, point: hits[0].point, distance: hits[0].distance };
        }
      }
    }
    return null;
  }

  function get3DStats() {
    return gpu.getStats();
  }
  function enablePixelation(factor = 2) {
    gpu.enablePixelation(factor);
  }
  function enableDithering(enabled = true) {
    gpu.enableDithering(enabled);
  }

  /**
   * setClearColor(color) - Set the background clear color
   * @param {number} color - Hex color value (e.g., 0xff0000 for red)
   */
  function setClearColor(color) {
    if (typeof gpu.setClearColor === 'function') {
      gpu.setClearColor(color);
    } else if (renderer?.setClearColor) {
      // Fallback for Three.js renderer
      renderer.setClearColor(color, 1.0);
    }
  }

  return {
    clearScene,
    setupScene,
    raycastFromCamera,
    get3DStats,
    enablePixelation,
    enableDithering,
    setClearColor,
  };
}
